<template>
  <div
    class="qaqc-dl-aux-distribution"
    :class="{ 'qaqc-consistency-check-wrap': hasSectionNote }"
  >
    <div class="table-content">
      <div class="table-wrap">
        <div class="table-title-div mt-4">
          <p class="table-title">{{ sectionTitle }}</p>
          <div class="qaqc-scorecard-header-actions">
           <div
              class="csv-btn csv-btn--plot"
              role="button"
              aria-label="Download plot as PNG"
              @click="downloadPlotPng"
            >
              <img
                class="pointer csv-btn-plot-icon"
                :src="plotImageDownload"
                alt="download plot as image"
              />
            </div>
            <div
              class="csv-btn"
              :class="{ 'csv-btn--busy': csvDownloadLoading }"
              role="button"
              :aria-busy="csvDownloadLoading"
              @click="downloadTableCSV"
            >
              <v-progress-circular
                v-if="csvDownloadLoading"
                indeterminate
                size="22"
                width="2"
                color="primary"
              />
              <v-img v-else class="pointer" :src="documentDownload" alt="csv download"></v-img>
            </div>
            <v-btn
              icon
              variant="text"
              size="medium"
              class="qaqc-scorecard-collapse-btn"
              :aria-label="isCollapsed ? 'Expand' : 'Collapse'"
              @click="isCollapsed = !isCollapsed"
            >
              <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    <div
      class="qaqc-overview-distribution qaqc-initial-screening-plots-layout"
      :class="{
        'qaqc-dl-aux-distribution-fullwidth': !hasPlotList,
        'qaqc-dl-aux-overview-with-note': hasSectionNote,
      }"
      v-show="!isCollapsed"
    >
      <p v-if="hasSectionNote" class="qaqc-consistency-desc qaqc-equilibrium-desc">{{ sectionNote }}</p>
      <div
        class="qaqc-dl-aux-chart-row"
        :class="{ 'qaqc-dl-aux-distribution-fullwidth': !hasPlotList }"
      >
        <div v-if="hasPlotList" class="qaqc-initial-screening-plot-selector qaqc-overview-plot-selector">
          <h6 class="qaqc-plot-selector-title">Distributions</h6>
          <button
            v-for="opt in plotOptions"
            :key="opt.id"
            type="button"
            :class="['qaqc-initial-screening-plot-item', { active: selectedPlotId === opt.id }]"
            @click="$emit('update:selectedPlotId', opt.id)"
          >
            {{ opt.label }}
          </button>
        </div>
        <div
          class="qaqc-initial-screening-plot-panel qaqc-overview-plot-panel"
          :class="{ 'qaqc-dl-aux-plot-panel-solo': !hasPlotList }"
        >
        <div>
          <div class="qaqc-test-chart-name-top qaqc-test-chart-name-top-distribution">
            <h6>{{ chartHeading }}</h6>
            <div
              v-if="showBinsSliderUi && !showNoData"
              class="qaqc-distribution-bins-block qaqc-distribution-modal-tab-div"
            >
              <div class="qaqc-distribution-tab-div">
                <p class="qaqc-distribution-tab-label">Number of bins:</p>
                <div class="qaqc-distribution-range-value">{{ numBins }}</div>
                <input
                  type="range"
                  class="slider qaqc-distribution-range-input"
                  min="2"
                  max="100"
                  step="1"
                  :value="numBins"
                  @input="onBinsRangeInput"
                />
              </div>
            </div>
          </div>
          <div class="qaqc-distribution-chart-inner">
            <div class="qaqc-distribution-chart-stack">
              <div ref="chartHostRef" class="qaqc-distribution-bar-chart qaqc-distribution-plotly-host"></div>
              <div
                v-show="showNoData"
                class="qaqc-distribution-no-data tabs-no-test-div qaqc-distribution-no-data-overlay"
              >
                <h4 class="qaqc-distribution-no-data-title">Data not available</h4>
              </div>
            </div>
            <div
              v-show="!showNoData && distributionLegendItems.length"
              class="qaqc-overview-chart-legend qaqc-distribution-legend qaqc-distribution-histogram-caption"
            >
              <span
                v-for="(item, idx) in distributionLegendItems"
                :key="'dist-leg-' + idx"
                class="qaqc-dist-legend-item legend-item"
              >
                <span class="qaqc-dist-legend-dot" :style="{ backgroundColor: item.color }"></span>
                <span class="qaqc-dist-legend-text">{{ item.label }}</span>
              </span>
            </div>
            <div v-if="hasDistributionBarClickPanel" class="qaqc-distribution-xaxis-chips-wrap">
              <span class="qaqc-consistency-sublabel">
                Non-compliant samples (click to view QC details)
              </span>
              <div
                v-if="
                  selectedDistributionBarIndex !== null &&
                  isRedDistributionBarIndex(selectedDistributionBarIndex)
                "
                class="qaqc-distribution-selected-bin-panel"
              >
                <div class="qaqc-distribution-selected-bin-header">
                  <span class="qaqc-distribution-selected-bin-title">{{
                    distributionCategories[selectedDistributionBarIndex]
                  }}</span>
                  <button type="button" class="qaqc-distribution-clear-bin" @click="clearSelectedDistributionBar">
                    Clear
                  </button>
                </div>
                <div
                  v-if="selectedDistributionBarChips.length"
                  class="qaqc-consistency-chips qaqc-distribution-bar-chips-row"
                >
                  <span
                    v-for="(chip, cidx) in selectedDistributionBarChips"
                    :key="distChipDomKey(selectedDistributionBarIndex, chip, cidx)"
                    class="qaqc-consistency-chip qaqc-consistency-check-chip"
                    :class="distChipClasses(selectedDistributionBarIndex, chip, cidx)"
                    role="button"
                    :tabindex="distChipTabindex(selectedDistributionBarIndex, chip, cidx)"
                    :aria-busy="distChipIsLoading(selectedDistributionBarIndex, chip, cidx)"
                    @click.stop="openDistSampleQc(chip, selectedDistributionBarIndex, cidx)"
                    @keydown.enter.prevent="activateDistChip(chip, selectedDistributionBarIndex, cidx)"
                    @keydown.space.prevent="activateDistChip(chip, selectedDistributionBarIndex, cidx)"
                  >
                    <v-progress-circular
                      v-if="distChipIsLoading(selectedDistributionBarIndex, chip, cidx)"
                      indeterminate
                      size="18"
                      width="2"
                      color="primary"
                      class="qaqc-consistency-check-chip-loader"
                    />
                    <template v-else>{{ chipLabel(chip) }}</template>
                  </span>
                </div>
                <div v-else-if="distributionBarApiLoading" class="qaqc-consistency-no-ids">Loading samples…</div>
                <div v-else class="qaqc-consistency-no-ids">No sample IDs for this category.</div>
              </div>
            </div>
            <div v-if="hasHistogramFailedChips" class="qaqc-distribution-hist-chips-wrap">
              <span class="qaqc-consistency-sublabel">Non-compliant samples (click to view QC details)</span>
              <div v-if="histogramApiLoading" class="qaqc-consistency-no-ids">Loading samples…</div>
              <template v-else>
                <div v-if="histogramFailedChipsList.length" class="qaqc-consistency-chips">
                  <span
                    v-for="(chip, cidx) in histogramFailedChipsList"
                    :key="distChipDomKey(-1, chip, cidx)"
                    class="qaqc-consistency-chip qaqc-consistency-check-chip"
                    :class="distChipClasses(-1, chip, cidx)"
                    role="button"
                    :tabindex="distChipTabindex(-1, chip, cidx)"
                    :aria-busy="distChipIsLoading(-1, chip, cidx)"
                    @click.stop="openDistSampleQc(chip, -1, cidx)"
                    @keydown.enter.prevent="activateDistChip(chip, -1, cidx)"
                    @keydown.space.prevent="activateDistChip(chip, -1, cidx)"
                  >
                    <v-progress-circular
                      v-if="distChipIsLoading(-1, chip, cidx)"
                      indeterminate
                      size="18"
                      width="2"
                      color="primary"
                      class="qaqc-consistency-check-chip-loader"
                    />
                    <template v-else>{{ chipLabel(chip) }}</template>
                  </span>
                </div>
                <div v-else class="qaqc-consistency-no-ids">No sample IDs for this category.</div>
              </template>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import { mapGetters } from "vuex";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { applyPlotlyGridLineStroke } from "@/utils/chartTheme";
import {
  distributionPlotColumnTitle,
  buildPrefixedDistributionFileName,
} from "./distributionCsvHelpers.js";
import { downloadPlotlyHostAsPng } from "./plotlyOverviewImageDownload.js";
import { buildDistributionPlotCsvStringFromApi } from "./distributionPlotCsvDownload.js";
import { drawQaqcDistributionPlotly } from "./distributionPlotlyDraw.js";
import { bindPlotlyHostResize, relayoutPlotlyHostElement } from "./plotlyChartResize.js";
import { createRedDistributionAxisTickBindings } from "./distributionPlotAxisTickClicks.js";
import {
  bindRedDistributionBarPointerMove,
  setDistributionRedBarHoverPointer,
} from "./distributionPlotRedBarCursor.js";
import {
  matrixContextToApiFilters,
  routerQueryFromApiFilters,
  pickSamplesListQueryForRouter,
  scorecardDatasetLabelToApiFilters,
} from "./qaqcSamplesListQuery.js";
import {
  fetchQaqcDistributionPlotSamples,
  histogramFailedSamplesRangeToken,
} from "./qaqcDistributionSamplesFetch.js";
import {
  resolveQaqcFailedSampleNavigation,
  pickTestFromSamplePayload,
  pushQaqcOverviewFailedSampleRoute,
} from "./qaqcFailedSampleNavigation.js";
import {
  distributionChipLabelForContext,
  qcDistributionTestNameForChipContext,
} from "./qaqcOverviewTab.js";

export default {
  name: "DlAuxDistributionSection",
  props: {
    sectionTitle: { type: String, required: true },
    sectionNote: { type: String, default: "" },
    plotOptions: { type: Array, default: () => [] },
    selectedPlotId: { type: String, default: "" },
    distributionData: {
      type: Object,
      required: true,
    },
    csvFileName: { type: String, default: "distribution_chart.csv" },
    qcDistributionTestName: { type: String, default: "dltests" },
    distributionChipNavContext: {
      type: String,
      default: "dl",
      validator: (v) => ["oil", "gas", "dl", "separator"].includes(v),
    },
  },
  emits: ["update:selectedPlotId"],
  data() {
    return {
      documentDownload: require("@/assets/Images/csv.svg"),
      plotImageDownload: require("@/assets/Svg/camIcon.svg"),
      isCollapsed: false,
      numBins: 10,
      unbindPlotlyHostResize: null,
      loadingDistChipKey: null,
      selectedDistributionBarIndex: null,
      _distributionPlotlyClickHandler: null,
      _distributionPlotlyHoverHandler: null,
      _distributionPlotlyUnhoverHandler: null,
      _distributionPlotlyAfterPlotHandler: null,
      _unbindRedBarPointerMove: null,
      _distAxisTickBindings: [],
      distributionBarApiChips: [],
      distributionBarApiLoading: false,
      histogramApiChips: [],
      histogramApiLoading: false,
      _distBarApiReq: 0,
      _histApiReq: 0,
      csvDownloadLoading: false,
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    hasSectionNote() {
      return String(this.sectionNote || "").trim().length > 0;
    },
    hasPlotList() {
      return Array.isArray(this.plotOptions) && this.plotOptions.length > 0;
    },
    plotKind() {
      return (this.distributionData && this.distributionData.plotKind) || "empty";
    },
    isHistogram() {
      return this.plotKind === "histogram";
    },
    isCategoricalBar() {
      return this.plotKind === "categoricalBar";
    },
    showBinsSliderUi() {
      const d = this.distributionData || {};
      if (d.plotKind !== "histogram" || d.showBinsSlider === false) return false;
      const raw = Array.isArray(d.rawValues) ? d.rawValues : [];
      if (raw.length <= 1) return false;
      const values = raw
        .map((v) => (typeof v === "string" ? parseFloat(v) : v))
        .filter((v) => Number.isFinite(v));
      if (values.length <= 1) return false;
      const first = values[0];
      return values.some((value) => value !== first);
    },
    distributionLegendItems() {
      const items = this.distributionData?.legendItems;
      return Array.isArray(items) ? items : [];
    },
    showNoData() {
      if (this.plotKind === "unavailable" || this.plotKind === "empty") return true;
      if (this.plotKind === "histogram") {
        const raw = this.distributionData?.rawValues;
        return !Array.isArray(raw) || raw.length === 0;
      }
      if (this.plotKind === "categoricalBar") {
        const cats = this.distributionData?.categories;
        return !Array.isArray(cats) || cats.length === 0;
      }
      return false;
    },
    selectedPlotLabel() {
      const opt = this.plotOptions.find((o) => o.id === this.selectedPlotId);
      return (opt && opt.label) || "";
    },
    chartHeading() {
      if (this.hasPlotList) {
        return this.selectedPlotLabel || this.sectionTitle;
      }
      return this.sectionTitle;
    },
    distributionCategories() {
      return this.distributionData?.categories || [];
    },
    outsideRangeChipFlagsList() {
      const f = this.distributionData?.outsideRangeChipFlags;
      return Array.isArray(f) ? f : [];
    },
    distributionRedBarFlagsList() {
      const n = this.distributionCategories.length;
      const explicit = this.outsideRangeChipFlagsList;
      if (Array.isArray(explicit) && explicit.length === n) return explicit.map(Boolean);
      const colors = Array.isArray(this.distributionData?.barColors) ? this.distributionData.barColors : [];
      const norm = (v) => String(v || "").replace(/\s+/g, "").toLowerCase();
      return Array.from({ length: n }, (_, i) => {
        const c = norm(colors[i]);
        return c === "#ef4444" || c === "rgb(239,68,68)" || c === "rgba(239,68,68,1)";
      });
    },
    distributionChipsByCategoryList() {
      const c = this.distributionData?.distributionChipsByCategory;
      return Array.isArray(c) ? c : [];
    },
    distributionPlotApiName() {
      return String(this.distributionData?.distributionPlotApiName ?? "").trim();
    },
    qcDistributionFluidType() {
      return this.distributionChipNavContext === "gas" ? "gas" : "oil";
    },
    hasDistributionBarClickPanel() {
      if (!this.isCategoricalBar || this.showNoData) return false;
      const chips = this.distributionChipsByCategoryList;
      const n = this.distributionCategories.length;
      if (!Array.isArray(chips) || n === 0 || chips.length !== n) return false;
      const hasRed = this.outsideRangeChipFlagsList.some(Boolean);
      if (!hasRed) return false;
      const hasAnyChips = chips.some((col) => Array.isArray(col) && col.length > 0);
      return hasAnyChips || hasRed;
    },
    selectedDistributionBarChips() {
      const i = this.selectedDistributionBarIndex;
      if (i == null || i < 0) return [];
      if (!this.isRedDistributionBarIndex(i)) return [];
      const mapperCol = this.distributionChipsByCategoryList[i];
      const mapper = Array.isArray(mapperCol) ? mapperCol : [];
      if (this.distributionBarApiLoading) return [];
      if (this.distributionBarApiChips.length) return this.distributionBarApiChips;
      return mapper;
    },
    histogramFailedChipsList() {
      const mapper = Array.isArray(this.distributionData?.failedHistogramChips)
        ? this.distributionData.failedHistogramChips
        : [];
      if (this.histogramApiLoading) return [];
      return this.histogramApiChips.length ? this.histogramApiChips : mapper;
    },
    hasHistogramFailedChips() {
      return this.isHistogram && !this.showNoData;
    },
    effectiveQcDistributionTestName() {
      return (
        qcDistributionTestNameForChipContext(this.distributionChipNavContext) ||
        this.qcDistributionTestName ||
        "dltests"
      );
    },
    distributionChipLabelPrefix() {
      return distributionChipLabelForContext(this.distributionChipNavContext);
    },
  },
  watch: {
    "$store.state.darkMode.isDarkMode"() {
      this.$nextTick(() => {
        this.renderChart();
        this.schedulePlotlyResize();
        this.attachPlotlyHostResize();
      });
    },
    selectedPlotId() {
      this.numBins = 10;
      this.loadingDistChipKey = null;
      this.selectedDistributionBarIndex = null;
      this.resetDistributionSampleApiState();
    },
    selectedDistributionBarIndex(idx) {
      this.distributionBarApiChips = [];
      this.distributionBarApiLoading = false;
      if (idx == null || idx < 0 || !this.isRedDistributionBarIndex(idx)) return;
      const name = this.distributionPlotApiName;
      const plotKey = this.distributionPlotKeyForCategoryIndex(idx);
      if (!name || plotKey.trim() === "") return;
      this.distributionBarApiLoading = true;
      const seq = ++this._distBarApiReq;
      fetchQaqcDistributionPlotSamples({
        testName: this.effectiveQcDistributionTestName,
        plotName: name,
        plotKey,
        fluidType: this.qcDistributionFluidType,
        listType: "range",
      })
        .then((chips) => {
          if (seq !== this._distBarApiReq || this.selectedDistributionBarIndex !== idx) return;
          this.distributionBarApiChips = Array.isArray(chips) ? chips : [];
        })
        .catch(() => {
          if (seq !== this._distBarApiReq || this.selectedDistributionBarIndex !== idx) return;
          this.distributionBarApiChips = [];
        })
        .finally(() => {
          if (seq !== this._distBarApiReq || this.selectedDistributionBarIndex !== idx) return;
          this.distributionBarApiLoading = false;
        });
    },
    distributionData: {
      deep: true,
      handler() {
        this.loadingDistChipKey = null;
        this.selectedDistributionBarIndex = null;
        this.resetDistributionSampleApiState();
        this.$nextTick(() => {
          this.renderChart();
          this.schedulePlotlyResize();
          this.attachPlotlyHostResize();
          this.loadHistogramDistributionSamples();
        });
      },
    },
    numBins() {
      if (this.isHistogram && !this.showNoData) {
        this.$nextTick(() => this.renderDistributionPlot());
        this.loadHistogramDistributionSamples();
      }
    },
    isCollapsed(v) {
      if (!v && (this.isHistogram || this.isCategoricalBar)) {
        this.$nextTick(() => {
          this.attachPlotlyHostResize();
          this.schedulePlotlyResize();
        });
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.renderChart();
      this.schedulePlotlyResize();
      this.attachPlotlyHostResize();
      this.loadHistogramDistributionSamples();
    });
    window.addEventListener("resize", this.onWindowResize);
  },
  beforeUnmount() {
    this.detachPlotlyHostResize();
    window.removeEventListener("resize", this.onWindowResize);
    this.purgePlotly();
  },
  methods: {
    resetDistributionSampleApiState() {
      this._distBarApiReq += 1;
      this._histApiReq += 1;
      this.distributionBarApiChips = [];
      this.distributionBarApiLoading = false;
      this.histogramApiChips = [];
      this.histogramApiLoading = false;
    },
    loadHistogramDistributionSamples() {
      this.histogramApiChips = [];
      this.histogramApiLoading = false;
      const d = this.distributionData || {};
      if (d.plotKind !== "histogram" || this.showNoData) return;
      const name = String(d.distributionPlotApiName ?? "").trim();
      if (!name) return;
      this.histogramApiLoading = true;
      const seq = ++this._histApiReq;
      const plotKey = histogramFailedSamplesRangeToken(this.numBins);
      fetchQaqcDistributionPlotSamples({
        testName: this.effectiveQcDistributionTestName,
        plotName: name,
        plotKey,
        fluidType: this.qcDistributionFluidType,
        listType: "range",
      })
        .then((chips) => {
          if (seq !== this._histApiReq) return;
          this.histogramApiChips = Array.isArray(chips) ? chips : [];
        })
        .catch(() => {
          if (seq !== this._histApiReq) return;
          this.histogramApiChips = [];
        })
        .finally(() => {
          if (seq !== this._histApiReq) return;
          this.histogramApiLoading = false;
        });
    },
    distributionPlotKeyForCategoryIndex(idx) {
      const apiKeys = this.distributionData?.distributionCategoryApiPlotKeys;
      if (Array.isArray(apiKeys) && idx >= 0 && idx < apiKeys.length) {
        const k = apiKeys[idx];
        if (k != null && String(k).trim() !== "") return String(k).trim();
      }
      const cat = this.distributionCategories[idx];
      return cat == null ? "" : String(cat);
    },
    clearSelectedDistributionBar() {
      this.selectedDistributionBarIndex = null;
    },
    isRedDistributionBarIndex(idx) {
      const flags = this.outsideRangeChipFlagsList;
      if (!Array.isArray(flags) || idx == null || !Number.isFinite(idx) || idx < 0) return false;
      return !!flags[idx];
    },
    unbindDistributionXAxisTickClicks() {
      for (const { el, fn } of this._distAxisTickBindings) {
        try {
          el.removeEventListener("click", fn);
        } catch {
          void 0;
        }
      }
      this._distAxisTickBindings = [];
    },
    unbindDistributionPlotlyInteractions() {
      const el = this.$refs.chartHostRef;
      const clickH = this._distributionPlotlyClickHandler;
      if (el && clickH && typeof el.removeListener === "function") {
        el.removeListener("plotly_click", clickH);
      } else if (el && typeof el.removeAllListeners === "function") {
        el.removeAllListeners("plotly_click");
      }
      this._distributionPlotlyClickHandler = null;

      const hoverH = this._distributionPlotlyHoverHandler;
      if (el && hoverH && typeof el.removeListener === "function") {
        el.removeListener("plotly_hover", hoverH);
      }
      this._distributionPlotlyHoverHandler = null;

      const unhoverH = this._distributionPlotlyUnhoverHandler;
      if (el && unhoverH && typeof el.removeListener === "function") {
        el.removeListener("plotly_unhover", unhoverH);
      }
      this._distributionPlotlyUnhoverHandler = null;
      if (typeof this._unbindRedBarPointerMove === "function") {
        this._unbindRedBarPointerMove();
        this._unbindRedBarPointerMove = null;
      }
      setDistributionRedBarHoverPointer(el, false);

      const afterH = this._distributionPlotlyAfterPlotHandler;
      if (el && afterH && typeof el.removeListener === "function") {
        el.removeListener("plotly_afterplot", afterH);
      }
      this._distributionPlotlyAfterPlotHandler = null;

      this.unbindDistributionXAxisTickClicks();
    },
    bindDistributionXAxisTickClicks() {
      this.unbindDistributionXAxisTickClicks();
      if (!this.isCategoricalBar) return;
      const host = this.$refs.chartHostRef;
      if (!host) return;
      const svg = host.querySelector("svg.main-svg") || host.querySelector("svg");
      if (!svg) return;

      const flags = this.distributionRedBarFlagsList;
      const bound = createRedDistributionAxisTickBindings({
        svg,
        categories: this.distributionCategories,
        flags,
        enableClicks: this.hasDistributionBarClickPanel,
        onTickClick: (idx, e) => {
          e.stopPropagation();
          this.selectedDistributionBarIndex =
            this.selectedDistributionBarIndex === idx ? null : idx;
        },
      });
      this._distAxisTickBindings.push(...bound);
    },
    distributionBarPathElements(svg, n) {
      if (!svg || !Number.isFinite(n) || n <= 0) return [];
      const barLayer =
        svg.querySelector("g.cartesianlayer .barlayer") ||
        svg.querySelector(".subplot .barlayer") ||
        svg.querySelector(".barlayer");
      if (!barLayer) return [];
      const barsGroup =
        barLayer.querySelector("g.trace.bars") ||
        barLayer.querySelector("g.bars") ||
        barLayer.querySelector(".bars");
      if (!barsGroup) return [];
      const direct = [...barsGroup.children].filter((el) => el.tagName === "path");
      if (direct.length === n) return direct;
      const nested = [...barsGroup.querySelectorAll("path")];
      return nested.length === n ? nested : [];
    },
    applyRedOnlyBarPointerStyle() {
      const host = this.$refs.chartHostRef;
      if (!host || !this.isCategoricalBar || !this.hasDistributionBarClickPanel) return;
      const svg = host.querySelector("svg.main-svg") || host.querySelector("svg");
      if (!svg) return;
      const flags = this.distributionRedBarFlagsList;
      const n = this.distributionCategories.length;
      const paths = this.distributionBarPathElements(svg, n);
      if (!paths.length) return;
      paths.forEach((path, i) => {
        if (i < flags.length && flags[i]) {
          path.style.cursor = "pointer";
          path.classList.add("qaqc-distribution-red-bar");
        } else {
          path.style.removeProperty("cursor");
          path.classList.remove("qaqc-distribution-red-bar");
        }
      });
    },
    setDistributionBarHoverPointer(active) {
      setDistributionRedBarHoverPointer(this.$refs.chartHostRef, !!active);
    },
    redDistributionBarPathElements() {
      const host = this.$refs.chartHostRef;
      if (!host || !this.isCategoricalBar) return [];
      const svg = host.querySelector("svg.main-svg") || host.querySelector("svg");
      if (!svg) return [];
      const flags = this.distributionRedBarFlagsList;
      const paths = this.distributionBarPathElements(svg, this.distributionCategories.length);
      return paths.filter((_, i) => i < flags.length && flags[i]);
    },
    bindRedDistributionBarPointerMove() {
      if (typeof this._unbindRedBarPointerMove === "function") {
        this._unbindRedBarPointerMove();
        this._unbindRedBarPointerMove = null;
      }
      const host = this.$refs.chartHostRef;
      if (!host || !this.hasDistributionBarClickPanel || !this.isCategoricalBar) return;
      this._unbindRedBarPointerMove = bindRedDistributionBarPointerMove(
        host,
        () => this.redDistributionBarPathElements(),
        (active) => this.setDistributionBarHoverPointer(active)
      );
    },
    bindDistributionPlotlyInteractions() {
      this.unbindDistributionPlotlyInteractions();
      if (!this.hasDistributionBarClickPanel || !this.isCategoricalBar) return;
      const el = this.$refs.chartHostRef;
      if (!el || typeof el.on !== "function") return;

      const barHandler = (ev) => {
        const pt = ev.points && ev.points[0];
        if (pt == null || pt.pointIndex === undefined) return;
        const idx = pt.pointIndex;
        if (!Number.isFinite(idx) || idx < 0 || idx >= this.distributionCategories.length) return;
        if (!this.isRedDistributionBarIndex(idx)) return;
        this.selectedDistributionBarIndex =
          this.selectedDistributionBarIndex === idx ? null : idx;
      };
      el.on("plotly_click", barHandler);
      this._distributionPlotlyClickHandler = barHandler;

      const hoverHandler = (ev) => {
        const pt = ev.points && ev.points[0];
        const idx = pt?.pointIndex;
        const isRed =
          Number.isFinite(idx) &&
          idx >= 0 &&
          idx < this.distributionCategories.length &&
          this.isRedDistributionBarIndex(idx);
        this.setDistributionBarHoverPointer(isRed);
      };
      el.on("plotly_hover", hoverHandler);
      this._distributionPlotlyHoverHandler = hoverHandler;

      const unhoverHandler = () => {
        this.setDistributionBarHoverPointer(false);
      };
      el.on("plotly_unhover", unhoverHandler);
      this._distributionPlotlyUnhoverHandler = unhoverHandler;

      const afterPlot = () => {
        requestAnimationFrame(() => {
          this.$nextTick(() => {
            this.bindDistributionXAxisTickClicks();
            this.applyRedOnlyBarPointerStyle();
            this.bindRedDistributionBarPointerMove();
          });
        });
      };
      el.on("plotly_afterplot", afterPlot);
      this._distributionPlotlyAfterPlotHandler = afterPlot;

      requestAnimationFrame(() => {
        this.$nextTick(() => {
          this.bindDistributionXAxisTickClicks();
          this.applyRedOnlyBarPointerStyle();
          this.bindRedDistributionBarPointerMove();
        });
      });
    },
    chipNavFiltersForRouter() {
      const ctx = this.distributionChipNavContext;
      if (ctx === "separator") return matrixContextToApiFilters("separator");
      if (ctx === "dl") return matrixContextToApiFilters("dl");
      if (ctx === "gas") return scorecardDatasetLabelToApiFilters("CCE Dataset (Gas)");
      return scorecardDatasetLabelToApiFilters("CCE Dataset (Oil)");
    },
    distChipInstanceKey(colIdx, chip, chipIdx) {
      const scope = colIdx >= 0 ? `c${colIdx}` : "hist";
      return `dist:${scope}:i${chipIdx}:${String(chip.id ?? "")}:f${String(chip.fluidSampleID ?? "")}`;
    },
    distChipDomKey(colIdx, chip, chipIdx) {
      return this.distChipInstanceKey(colIdx, chip, chipIdx);
    },
    distChipIsLoading(colIdx, chip, chipIdx) {
      return this.loadingDistChipKey === this.distChipInstanceKey(colIdx, chip, chipIdx);
    },
    distChipIsBusyOther(colIdx, chip, chipIdx) {
      return this.loadingDistChipKey != null && !this.distChipIsLoading(colIdx, chip, chipIdx);
    },
    distChipClasses(colIdx, chip, chipIdx) {
      return {
        "qaqc-consistency-check-chip--loading": this.distChipIsLoading(colIdx, chip, chipIdx),
        "qaqc-consistency-check-chip--disabled": this.distChipIsBusyOther(colIdx, chip, chipIdx),
      };
    },
    distChipTabindex(colIdx, chip, chipIdx) {
      if (this.distChipIsLoading(colIdx, chip, chipIdx)) return -1;
      if (this.distChipIsBusyOther(colIdx, chip, chipIdx)) return -1;
      return 0;
    },
    chipLabel(chip) {
      const fid = chip.fluidSampleID || chip.id || "";
      if (!fid) return "";
      return `${this.distributionChipLabelPrefix} - ${fid}`;
    },
    resolveTenantId() {
      return getTenantPrefix() || "";
    },
    pushFailedSampleTestRoute(sampleId, routeTestType, testId, wellName, navigationTarget) {
      const listQuery = {
        ...routerQueryFromApiFilters(this.chipNavFiltersForRouter()),
        ...pickSamplesListQueryForRouter(this.$route.query || {}),
      };
      pushQaqcOverviewFailedSampleRoute(this.$router, {
        tenantId: this.resolveTenantId(),
        mongoSampleId: sampleId,
        testId,
        routeTestType,
        well: wellName,
        navigationTarget,
        listQuery,
      });
    },
    distributionNavigationContext() {
      const criterionName = this.hasPlotList
        ? [this.selectedPlotLabel, this.selectedPlotId, this.sectionTitle].filter(Boolean).join(" ")
        : this.sectionTitle || "";
      const isSeparator = this.distributionChipNavContext === "separator";
      return {
        testKind: isSeparator ? "separator" : "dl",
        source: "dlDistribution",
        criterionName,
      };
    },
    samplePayloadFromViewSummary(res) {
      const d = res?.data?.data;
      if (!d) return null;
      if (d.sample && typeof d.sample === "object") return d.sample;
      const list = d.samples;
      if (Array.isArray(list) && list[0]) return list[0];
      return null;
    },
    async fetchSamplePayloadForChip(chip) {
      if (chip.id) {
        const res = await api.get(`public/api/samples/${chip.id}?viewSummary=true`);
        return this.samplePayloadFromViewSummary(res);
      }
      const fid = chip.fluidSampleID;
      if (!fid) return null;
      const res = await api.get("public/api/samples", {
        params: {
          fluidSampleID: `["${String(fid)}"]`,
          viewSummary: true,
          pageNumber: 1,
          pageSize: 10,
        },
      });
      const samples = res?.data?.data?.samples;
      return Array.isArray(samples) && samples[0] ? samples[0] : null;
    },
    activateDistChip(chip, colIdx, chipIdx) {
      if (this.loadingDistChipKey) return;
      this.openDistSampleQc(chip, colIdx, chipIdx);
    },
    async openDistSampleQc(chip, colIdx, chipIdx) {
      const key = this.distChipInstanceKey(colIdx, chip, chipIdx);
      if (!key || this.loadingDistChipKey) return;
      this.loadingDistChipKey = key;
      try {
        const s = await this.fetchSamplePayloadForChip(chip);
        if (!s) return;
        const mongoId = s.ID || s._id || s.id;
        if (!mongoId) return;

        const navCtx = this.distributionNavigationContext();
        const { testId, routeTestType } = pickTestFromSamplePayload(s, navCtx.testKind);
        const navigationTarget = resolveQaqcFailedSampleNavigation(navCtx);

        if (testId && routeTestType) {
          const well = s.location?.well ?? s.wellName ?? s.well;
          this.pushFailedSampleTestRoute(mongoId, routeTestType, testId, well, navigationTarget);
        } else {
          const well = s.location?.well ?? s.wellName ?? s.well;
          const q = {
            type: "wells",
            ...routerQueryFromApiFilters(this.chipNavFiltersForRouter()),
            ...pickSamplesListQueryForRouter(this.$route.query || {}),
          };
          if (well) {
            this.$router.push({ name: "QAQCDetailView", params: { well: String(well) }, query: q });
          } else {
            this.$router.push({ name: "QAQCDetailView", query: q });
          }
        }
      } catch {
        void 0;
      } finally {
        this.loadingDistChipKey = null;
      }
    },
    attachPlotlyHostResize() {
      this.detachPlotlyHostResize();
      const el = this.$refs.chartHostRef;
      if (!el) return;
      this.unbindPlotlyHostResize = bindPlotlyHostResize(el, this.schedulePlotlyResize);
    },
    detachPlotlyHostResize() {
      if (typeof this.unbindPlotlyHostResize === "function") {
        this.unbindPlotlyHostResize();
        this.unbindPlotlyHostResize = null;
      }
    },
    onBinsRangeInput(e) {
      const n = Number(e.target.value);
      if (Number.isFinite(n)) this.numBins = n;
    },
    schedulePlotlyResize() {
      requestAnimationFrame(() => {
        this.onWindowResize();
        requestAnimationFrame(() => {
          this.onWindowResize();
        });
      });
    },
    applyChartThemeGrid() {
      const el = this.$refs.chartHostRef;
      if (!el || this.showNoData) return;
      applyPlotlyGridLineStroke(el, this.isDarkMode);
    },
    onWindowResize() {
      const el = this.$refs.chartHostRef;
      if (!el || this.showNoData || (!this.isHistogram && !this.isCategoricalBar)) return;
      relayoutPlotlyHostElement(el);
      this.applyChartThemeGrid();
      if (this.isCategoricalBar) {
        requestAnimationFrame(() => {
          this.$nextTick(() => {
            this.bindDistributionXAxisTickClicks();
            this.applyRedOnlyBarPointerStyle();
          });
        });
      }
    },
    purgePlotly() {
      this.unbindDistributionPlotlyInteractions();
      const el = this.$refs.chartHostRef;
      if (!el) return;
      try {
        Plotly.purge(el);
      } catch {
        /* container may be empty */
      }
    },
    renderChart() {
      this.purgePlotly();
      const el = this.$refs.chartHostRef;
      if (this.showNoData) {
        if (el) el.innerHTML = "";
        return;
      }
      if (!el) return;
      el.innerHTML = "";
      if (this.isHistogram || this.isCategoricalBar) this.renderDistributionPlot();
    },
    renderDistributionPlot() {
      const el = this.$refs.chartHostRef;
      if (!el || this.showNoData) return;
      const drawn = drawQaqcDistributionPlotly(el, this.distributionData, this.numBins);
      const afterDraw = () => {
        this.applyChartThemeGrid();
        this.schedulePlotlyResize();
        this.$nextTick(() => {
          this.bindDistributionXAxisTickClicks();
          this.bindDistributionPlotlyInteractions();
        });
      };
      if (drawn && typeof drawn.then === "function") {
        drawn.then(() => afterDraw());
      } else {
        afterDraw();
      }
    },
    async downloadPlotPng() {
      if (this.showNoData || (!this.isHistogram && !this.isCategoricalBar)) {
        this.$store?.commit?.("api/showSnackbar", {
          message: "No plot to export.",
          color: "#374151",
          timeout: 4000,
        });
        return;
      }
      const el = this.$refs.chartHostRef;
      const fallbackTitle = this.chartHeading;
      const base = buildPrefixedDistributionFileName(
        this.csvFileName,
        this.distributionData,
        fallbackTitle,
        this.selectedPlotId
      ).replace(/\.csv$/i, "");
      const ok = await downloadPlotlyHostAsPng(el, base, {
        title: fallbackTitle || this.selectedPlotId,
      });
      if (!ok) {
        this.$store?.commit?.("api/showSnackbar", {
          message: "Plot image export failed.",
          color: "#B71C1C",
          timeout: 5000,
        });
      }
    },
    async downloadTableCSV() {
      if (this.csvDownloadLoading) return;
      if (this.showNoData || (!this.isHistogram && !this.isCategoricalBar)) return;
      const plotName = this.distributionPlotApiName;
      if (!plotName) {
        this.$store?.commit?.("api/showSnackbar", {
          message: "Cannot export: plot name is missing.",
          color: "#B71C1C",
          timeout: 5000,
        });
        return;
      }
      const fallbackTitle = this.chartHeading;
      const plotColumnHeader = distributionPlotColumnTitle(this.distributionData, fallbackTitle);
      this.csvDownloadLoading = true;
      try {
        const { csv, rowCount } = await buildDistributionPlotCsvStringFromApi({
          plotName,
          qcDistributionTestName: this.effectiveQcDistributionTestName,
          qcDistributionFluidType: this.qcDistributionFluidType,
          plotColumnHeader,
        });
        if (!rowCount) {
          this.$store?.commit?.("api/showSnackbar", {
            message: "No samples returned for CSV export.",
            color: "#374151",
            timeout: 5000,
          });
        }
        const downloadName = buildPrefixedDistributionFileName(
          this.csvFileName,
          this.distributionData,
          fallbackTitle,
          this.selectedPlotId
        );
        const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", downloadName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } catch {
        this.$store?.commit?.("api/showSnackbar", {
          message: "CSV export failed.",
          color: "#B71C1C",
          timeout: 5000,
        });
      } finally {
        this.csvDownloadLoading = false;
      }
    },
  },
};
</script>
